import React, { createContext, useState, useContext, useEffect } from 'react';
import { login as apiLogin, getMyPermissions } from '../utils/api';
import toast from 'react-hot-toast';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('token');
    const storedPermissions = localStorage.getItem('permissions');
    const storedIsSuperAdmin = localStorage.getItem('is_super_admin');
    
    console.log('AuthProvider init - user:', storedUser ? 'Exists' : 'None');
    console.log('AuthProvider init - token:', storedToken ? 'Exists' : 'None');
    console.log('AuthProvider init - permissions:', storedPermissions || 'None');
    
    if (storedUser && storedToken) {
      try {
        const parsedUser = JSON.parse(storedUser);
        console.log('AuthProvider - Setting user from localStorage:', parsedUser.email);
        
        // Set user state
        setUser(parsedUser);
        
        // Set permissions if available
        if (storedPermissions) {
          const parsedPermissions = JSON.parse(storedPermissions);
          setPermissions(parsedPermissions);
          console.log('AuthProvider - Set permissions from storage:', parsedPermissions.length);
        }
        
        // Set super admin status
        if (storedIsSuperAdmin) {
          const parsedSuperAdmin = JSON.parse(storedIsSuperAdmin);
          setIsSuperAdmin(parsedSuperAdmin);
          console.log('AuthProvider - Is super admin:', parsedSuperAdmin);
        } else if (parsedUser.role === 'super_admin') {
          setIsSuperAdmin(true);
          localStorage.setItem('is_super_admin', 'true');
        }
        
        // If user is admin/super_admin but permissions not loaded, fetch them
        if ((parsedUser.role === 'admin' || parsedUser.role === 'super_admin') && 
            (!storedPermissions || storedPermissions === '[]')) {
          console.log('AuthProvider - Fetching permissions from API...');
          fetchPermissions();
        }
      } catch (error) {
        console.error('Error parsing stored data:', error);
        clearAuthData();
      }
    }
    setLoading(false);
  }, []);

  const fetchPermissions = async () => {
    try {
      console.log('fetchPermissions called');
      const response = await getMyPermissions();
      console.log('Permissions API response:', response);
      
      const userPermissions = response.permissions || [];
      const userIsSuperAdmin = response.is_super_admin || false;
      
      setPermissions(userPermissions);
      setIsSuperAdmin(userIsSuperAdmin);
      
      // Store in localStorage
      localStorage.setItem('permissions', JSON.stringify(userPermissions));
      localStorage.setItem('is_super_admin', JSON.stringify(userIsSuperAdmin));
      
      console.log('Permissions fetched and stored:', userPermissions);
    } catch (error) {
      console.error('Error fetching permissions:', error);
      // Don't clear auth data on permission fetch failure
    }
  };

  const signIn = async (email, password) => {
    console.log('signIn called with:', email);
    
    try {
      const response = await apiLogin({ email, password });
      console.log('API login response:', response);
      
      // response should be {token, user, permissions, is_super_admin}
      const { token, user: userData, permissions: userPermissions = [], is_super_admin = false } = response;
      
      console.log('Token received:', token ? 'Yes' : 'No');
      console.log('User data received:', userData);
      console.log('Permissions received:', userPermissions);
      console.log('Is super admin:', is_super_admin);
      
      if (!token || !userData) {
        throw new Error('Invalid response from server');
      }
      
      // Store in localStorage
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('token', token);
      localStorage.setItem('permissions', JSON.stringify(userPermissions));
      localStorage.setItem('is_super_admin', JSON.stringify(is_super_admin));
      console.log('Stored in localStorage');
      
      // Update state
      setUser(userData);
      setPermissions(userPermissions);
      setIsSuperAdmin(is_super_admin);
      
      console.log('User state updated:', userData.email);
      console.log('Permissions updated:', userPermissions);
      console.log('Super admin status:', is_super_admin);
      
      return { 
        success: true, 
        user: userData,
        permissions: userPermissions,
        is_super_admin: is_super_admin
      };
    } catch (error) {
      console.error('SignIn error:', error);
      console.error('Error details:', error.response?.data);
      toast.error(error.response?.data?.message || 'Login failed');
      return { 
        success: false, 
        message: error.response?.data?.message || 'Login failed' 
      };
    }
  };

  const signOut = () => {
    console.log('signOut called');
    clearAuthData();
    toast.success('Logged out successfully');
    window.location.href = '/login';
  };

  const clearAuthData = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('permissions');
    localStorage.removeItem('is_super_admin');
    setUser(null);
    setPermissions([]);
    setIsSuperAdmin(false);
  };

  const isAdmin = () => {
    const admin = user?.role === 'admin' || user?.role === 'super_admin';
    console.log('isAdmin check:', admin, 'User role:', user?.role);
    return admin;
  };

  const hasPermission = (requiredPermission) => {
    if (!user) return false;
    
    // Super admin has all permissions
    if (isSuperAdmin) return true;
    
    // Admin needs specific permission
    if (user.role === 'admin') {
      const hasPerm = permissions.includes(requiredPermission);
      console.log(`hasPermission check for "${requiredPermission}":`, hasPerm);
      return hasPerm;
    }
    
    // Regular users have no admin permissions
    return false;
  };

  const hasAnyPermission = (requiredPermissions) => {
    if (!user) return false;
    
    // Super admin has all permissions
    if (isSuperAdmin) return true;
    
    // Admin needs any of the required permissions
    if (user.role === 'admin') {
      const hasAny = requiredPermissions.some(perm => permissions.includes(perm));
      console.log(`hasAnyPermission check for ${requiredPermissions}:`, hasAny);
      return hasAny;
    }
    
    return false;
  };

  const refreshPermissions = async () => {
    if (isAdmin()) {
      await fetchPermissions();
    }
  };

  const value = {
    user,
    permissions,
    isSuperAdmin,
    loading,
    signIn,
    signOut,
    isAdmin,
    hasPermission,
    hasAnyPermission,
    refreshPermissions
  };

  console.log('AuthProvider render - user:', user?.email, 
    'loading:', loading, 
    'permissions:', permissions.length,
    'isSuperAdmin:', isSuperAdmin);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};